import React from "react";
import classNames from "classnames";
import { Icomoon, PriceInCurrency } from "../..";
import { addDays, formatDate } from "@travellocal/utils/dateHelpers";
import { Icon } from "../../brand";
import "./ItinerarySummaryInfo.scss";

export interface ItinerarySummaryInfoProps {
  /**
   * Number of passengers
   */
  numberOfPeople: number;

  /**
   * Trip start date
   */
  startDate: IDate;

  /**
   * Trip length
   */
  numberOfDays: number;

  /**
   * Itinerary price
   */
  totalPrice: IPriceInCurrency;

  /**
   * onClick handler to initiate scrolling to Included Services section of itinerary
   */
  onClickIncludedServices: () => void;

  /**
   * onClick handler to initiate scrolling to Pricing section of itinerary
   */
  onClickPricingDetails: () => void;

  className?: string;
}

/**
 * Displays summary of key information at the top of the customer itinerary page.
 * 
 * @status stable
 */
export const ItinerarySummaryInfo: React.FC<ItinerarySummaryInfoProps> = ({
  numberOfPeople, startDate, numberOfDays, totalPrice, onClickIncludedServices, onClickPricingDetails, className
}) => {
  const endDate = addDays(startDate, numberOfDays - 1);

  return (
    <div className={classNames("itinerary-summary-info", className)}>
      <div className="itinerary-summary-info__row columns is-paddingless">
        <div className="column is-one-third-desktop is-half-tablet">
          <div className="itinerary-summary-info__detail">
            <Icon className="itinerary-summary-info__detail-icon" icomoon={Icomoon.users} />
            {numberOfPeople} traveller{numberOfPeople > 1 ? `s` : null}
          </div>
        </div>
        <div className="column is-one-third-desktop is-half-tablet">
          <div className="itinerary-summary-info__detail">
            <Icon className="itinerary-summary-info__detail-icon" icomoon={Icomoon.calendar} />
            {numberOfDays} days
          </div>
        </div>
      </div>
      <div className="itinerary-summary-info__row columns is-paddingless">
        <div className="column is-one-third-desktop is-half-tablet">
          <div className="itinerary-summary-info__detail">
            <Icon className="itinerary-summary-info__detail-icon" icomoon={Icomoon.airplane} />
            {formatDate(startDate, "ddd Do MMM")} - {formatDate(endDate, "ddd Do MMM")}
          </div>
        </div>
        <div className="column is-one-third-desktop is-half-tablet">
          <div className="itinerary-summary-info__link-wrapper">
            <Icon className="itinerary-summary-info__detail-icon" icomoon={Icomoon.checkmark3} />
            <a
              onClick={onClickIncludedServices}
              className="secondary-button itinerary-summary-info__link"
            >
              <span>What&apos;s included? <Icon icomoon={Icomoon.arrowDown3} /></span>
            </a>
          </div>
        </div>
      </div>
      <div className="itinerary-summary-info__row columns is-paddingless">
        <div className="column is-one-third-desktop">
          <div className="itinerary-summary-info__link-wrapper">
            <Icon className="itinerary-summary-info__detail-icon" icomoon={Icomoon.cash2} />
            <span className="itinerary-summary-info__price">
              <PriceInCurrency displayForCustomer priceInCurrency={totalPrice} />{" "}
            </span>
            <a
              onClick={onClickPricingDetails}
              className="secondary-button itinerary-summary-info__link"
            >
              <span>Pricing details <Icon icomoon={Icomoon.arrowDown3} /></span>
            </a> 
          </div>
        </div>
      </div>
    </div>
  );
}
